import apiClient from './api-client';
import type {
  ListResponse, EntityResponse, ListQuery,
  Company, User, UserRoleServiceModel, Site, Well, WellServiceModel,
  Annotation, WellAnnotation, ToolModel, Tool, ToolServiceRecord, ChangePasswordModel,
  WellServiceModelResponse, CloudProcessItem, SummaryDashboard, GeonomicFileItem,
} from '../types/api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function queryString(q: any): string {
  const params = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
  });
  return params.toString() ? `?${params}` : '';
}

// Backend returns HTTP 200 even when a business rule fails (e.g. duplicate name),
// signalling the failure via { status: "Failed", error: { errorMessage } } in the body.
// Unwrap EntityResponse envelopes so callers `await`-ing a mutation get a real
// rejection with the server message surfaced to the UI.
function unwrapEntity<T>(res: EntityResponse<T>): EntityResponse<T> {
  if (res && res.status && res.status !== 'Successfull') {
    throw new Error(res.error?.errorMessage || 'Operation failed');
  }
  return res;
}

// ---- Companies ----
export const companyApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Company>>(`/api/companies/getallcompany${queryString(q)}`).then(r => r.data),
  getAllActive: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Company>>(`/api/companies/getallactivecompany${queryString(q)}`).then(r => r.data),
  getById: (companyId: number) =>
    apiClient.get<EntityResponse<Company>>(`/api/companies/getbyidcompany?companyId=${companyId}`).then(r => r.data),
  create: (entity: Partial<Company>) =>
    apiClient.post<EntityResponse<Company>>('/api/companies/insertcompany', entity).then(r => unwrapEntity(r.data)),
  update: (entity: Partial<Company>) =>
    apiClient.put<EntityResponse<Company>>('/api/companies/updatecompany', entity).then(r => unwrapEntity(r.data)),
  delete: (companyId: number) =>
    apiClient.delete<EntityResponse<Company>>(`/api/companies/deletecompany?companyId=${companyId}`).then(r => unwrapEntity(r.data)),
};

// ---- Users ----
export const userApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<User>>(`/api/users/getalluser${queryString(q)}`).then(r => r.data),
  getAllByCompanyId: (companyId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<User>>(`/api/users/getalluserbycompanyid${queryString({ ...q, companyId })}`).then(r => r.data),
  getById: (userId: number) =>
    apiClient.get<EntityResponse<User>>(`/api/users/getbyiduser?userId=${userId}`).then(r => r.data),
  create: (entity: UserRoleServiceModel) =>
    apiClient.post<EntityResponse<User>>('/api/users/insertuser', entity).then(r => r.data),
  update: (entity: UserRoleServiceModel) =>
    apiClient.put<EntityResponse<User>>('/api/users/updateuser', entity).then(r => r.data),
  delete: (userId: number) =>
    apiClient.post<EntityResponse<User>>(`/api/users/deleteuser?userId=${userId}`).then(r => r.data),
  changePassword: (model: ChangePasswordModel) =>
    apiClient.post<EntityResponse<User>>('/api/users/changepassword', model).then(r => r.data),
};

// ---- Sites ----
export const siteApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Site>>(`/api/sites/getallsite${queryString(q)}`).then(r => r.data),
  getAllByCompanyId: (companyId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<Site>>(`/api/sites/getallsitebycompanyid${queryString({ ...q, companyId })}`).then(r => r.data),
  getById: (siteId: number) =>
    apiClient.get<EntityResponse<Site>>(`/api/sites/getbyidsite?siteId=${siteId}`).then(r => r.data),
  create: (entity: Partial<Site>) =>
    apiClient.post<EntityResponse<Site>>('/api/sites/insertsite', entity).then(r => r.data),
  update: (entity: Partial<Site>) =>
    apiClient.put<EntityResponse<Site>>('/api/sites/updatesite', entity).then(r => r.data),
  delete: (entity: Partial<Site>) =>
    apiClient.post<EntityResponse<Site>>('/api/sites/deletesite', entity).then(r => r.data),
};

// ---- Wells ----
export const wellApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Well>>(`/api/wells/getallwell${queryString(q)}`).then(r => r.data),
  getAllByCompanyId: (companyId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<Well>>(`/api/wells/getallwellbycompanyid${queryString({ ...q, companyId })}`).then(r => r.data),
  getAllBySiteId: (siteId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<Well>>(`/api/wells/getallwellbysiteid${queryString({ ...q, siteId })}`).then(r => r.data),
  getById: (wellId: number) =>
    apiClient.get<EntityResponse<Well>>(`/api/wells/getbyidwell?wellId=${wellId}`).then(r => r.data),
  create: (entity: Partial<WellServiceModel>) =>
    apiClient.post<EntityResponse<Well>>('/api/wells/insertwell', entity).then(r => r.data),
  update: (entity: Partial<WellServiceModel>) =>
    apiClient.put<EntityResponse<Well>>('/api/wells/updatewell', entity).then(r => r.data),
  delete: (well: Partial<Well>) =>
    apiClient.post<EntityResponse<Well>>('/api/wells/deletewell', well).then(r => r.data),
  uploadCsv: (file: File, companyId: number, siteId: number) => {
    const form = new FormData();
    form.append('files', file);
    return apiClient.post<ListResponse<Well>>(`/api/wells/upload?companyId=${companyId}&siteId=${siteId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  uploadLas: (file: File, companyId: number, siteId: number) => {
    const form = new FormData();
    form.append('files', file);
    return apiClient.post<ListResponse<Well>>(`/api/wells/uploadlasfile?companyId=${companyId}&siteId=${siteId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  uploadLasForWell: (
    file: File, wellId: number, companyId: number,
    onProgress?: (pct: number) => void,
    abortSignal?: AbortSignal,
  ) => {
    const fd = new FormData();
    fd.append('files', file);
    return apiClient.post<number>(
      `/api/CaliperData/upload_LasFileWithDrillSurveyData?fileName=${encodeURIComponent(file.name)}&wellId=${wellId}&companyId=${companyId}`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortSignal,
        onUploadProgress: (e) => {
          if (!onProgress || !e.total) return;
          onProgress(Math.round((e.loaded / e.total) * 100));
        },
      }
    ).then(r => r.data);
  },
  getWellServiceModel: (wellId: number) =>
    apiClient.get<EntityResponse<WellServiceModelResponse>>(`/api/wells/getWellServiceModleById?wellId=${wellId}`).then(r => r.data),
  uploadClientFile: (file: File, companyId: number, wellId: number) => {
    const fd = new FormData();
    fd.append('files', file);
    return apiClient.post<ListResponse<Well>>(`/api/wells/uploadfile?companyId=${companyId}&siteId=0&wellId=${wellId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  /** Chunked upload for large files (10MB chunks). onProgress(0-100). */
  uploadClientFileChunked: async (
    file: File, companyId: number, wellId: number,
    onProgress?: (pct: number) => void,
    abortSignal?: AbortSignal,
  ) => {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10 MB
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < totalChunks; i++) {
      if (abortSignal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      const start = i * CHUNK_SIZE;
      const end = Math.min(file.size, start + CHUNK_SIZE);
      const chunk = file.slice(start, end);
      const fd = new FormData();
      fd.append('chunk', chunk);
      await apiClient.post(
        `/api/wells/uploadChunk?chunkIndex=${i}&totalChunks=${totalChunks}&fileName=${encodeURIComponent(file.name)}&companyId=${companyId}&siteId=0&wellId=${wellId}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' }, signal: abortSignal },
      );
      onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
    }
  },
  downloadFile: (fileName: string, companyId: number, wellId: number) =>
    apiClient.get(`/api/wells/DownloadFileInChunks?fileName=${encodeURIComponent(fileName)}&companyId=${companyId}&siteId=0&wellId=${wellId}`, {
      responseType: 'blob',
    }).then(r => r.data as Blob),
  downloadLasFile: (geonomicFileId: number, companyId: number, wellId: number) =>
    apiClient.get(`/api/wells/DownloadLasFile?geonomicFileId=${geonomicFileId}&companyId=${companyId}&wellId=${wellId}`, {
      responseType: 'blob',
    }).then(r => r.data as Blob),
  deleteFile: (fileName: string, companyId: number, wellId: number) =>
    apiClient.delete(`/api/wells/DeleteFile?fileName=${encodeURIComponent(fileName)}&companyId=${companyId}&siteId=0&wellId=${wellId}`).then(r => r.data),
};

// ---- Well Annotations ----
export const wellAnnotationApi = {
  getByWellId: (wellId: number) =>
    apiClient.get<ListResponse<WellAnnotation>>(`/api/wells/GetWellAnnotations?wellId=${wellId}`).then(r => r.data),
  add: (model: { wellId: number; annotationId: number; depth: number; shortDescription: string; longDescription: string }) =>
    apiClient.post<EntityResponse<WellAnnotation>>('/api/wells/AddWellAnnotation', model).then(r => r.data),
  update: (model: { id: number; wellId: number; annotationId: number; depth: number; shortDescription: string; longDescription: string }) =>
    apiClient.put<EntityResponse<WellAnnotation>>('/api/wells/UpdateWellAnnotation', model).then(r => r.data),
  delete: (id: number) =>
    apiClient.delete<EntityResponse<boolean>>(`/api/wells/DeleteWellAnnotation?id=${id}`).then(r => r.data),
};

// ---- Annotations ----
export const annotationApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Annotation>>(`/api/annotations/getallannotations${queryString(q)}`).then(r => r.data),
  getById: (annotationId: number) =>
    apiClient.get<EntityResponse<Annotation>>(`/api/annotations/getbyidannotation?annotationId=${annotationId}`).then(r => r.data),
  create: (entity: Partial<Annotation>) =>
    apiClient.post<EntityResponse<Annotation>>('/api/annotations/insertannotation', entity).then(r => r.data),
  update: (entity: Partial<Annotation>) =>
    apiClient.put<EntityResponse<Annotation>>('/api/annotations/updateannotation', entity).then(r => r.data),
  delete: (annotationId: number) =>
    apiClient.delete<EntityResponse<Annotation>>(`/api/annotations/deleteannotation?annotationId=${annotationId}`).then(r => r.data),
};

// ---- Tool Models ----
export const toolModelApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<ToolModel>>(`/api/toolmodels/getalltoolmodel${queryString(q)}`).then(r => r.data),
  getById: (toolModelId: number) =>
    apiClient.get<EntityResponse<ToolModel>>(`/api/toolmodels/getbyidtoolmodel?toolModelId=${toolModelId}`).then(r => r.data),
  create: (entity: Partial<ToolModel>) =>
    apiClient.post<EntityResponse<ToolModel>>('/api/toolmodels/inserttoolmodel', entity).then(r => r.data),
  update: (entity: Partial<ToolModel>) =>
    apiClient.put<EntityResponse<ToolModel>>('/api/toolmodels/updatetoolmodel', entity).then(r => r.data),
  delete: (toolModelId: number) =>
    apiClient.delete<EntityResponse<ToolModel>>(`/api/toolmodels/deletetoolmodel?toolModelId=${toolModelId}`).then(r => r.data),
};

// ---- Tools (Inventory) ----
export const toolApi = {
  getAll: (q: ListQuery = {}) =>
    apiClient.get<ListResponse<Tool>>(`/api/tools/getalltool${queryString(q)}`).then(r => r.data),
  getAllByCompanyId: (companyId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<Tool>>(`/api/tools/getalltoolbycompanyid${queryString({ ...q, companyId })}`).then(r => r.data),
  getById: (toolId: number) =>
    apiClient.get<EntityResponse<Tool>>(`/api/tools/getbyidtool?toolId=${toolId}`).then(r => r.data),
  create: (entity: Partial<Tool>) =>
    apiClient.post<EntityResponse<Tool>>('/api/tools/inserttool', entity).then(r => r.data),
  update: (entity: Partial<Tool>) =>
    apiClient.put<EntityResponse<Tool>>('/api/tools/updatetool', entity).then(r => r.data),
  delete: (toolId: number) =>
    apiClient.delete<EntityResponse<Tool>>(`/api/tools/deletetool?toolId=${toolId}`).then(r => r.data),
};

// ---- Tool Service Records ----
export const toolServiceRecordApi = {
  getAllByToolId: (toolId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<ToolServiceRecord>>(`/api/toolservicerecords/getalltoolservicerecordbytoolid${queryString({ ...q, toolId })}`).then(r => r.data),
  create: (entity: Partial<ToolServiceRecord>) =>
    apiClient.post<EntityResponse<ToolServiceRecord>>('/api/toolservicerecords/inserttoolservicerecord', entity).then(r => r.data),
  update: (entity: Partial<ToolServiceRecord>) =>
    apiClient.put<EntityResponse<ToolServiceRecord>>('/api/toolservicerecords/updatetoolservicerecord', entity).then(r => r.data),
  delete: (toolServiceRecordId: number) =>
    apiClient.delete<EntityResponse<ToolServiceRecord>>(`/api/toolservicerecords/deletetoolservicerecord?toolServiceRecordId=${toolServiceRecordId}`).then(r => r.data),
};

// ---- Roles ----
export const roleApi = {
  getAll: () =>
    apiClient.get<ListResponse<{ id: number; name: string }>>('/api/roles/getallroles').then(r => r.data),
  getAdminRoles: () =>
    apiClient.get<ListResponse<{ id: number; name: string }>>('/api/roles/getadminsroles').then(r => r.data),
  getUserRoles: () =>
    apiClient.get<ListResponse<{ id: number; name: string }>>('/api/roles/getusersroles').then(r => r.data),
};

// ---- Cloud Process (3D Models) ----
export const cloudProcessApi = {
  getAllByWellId: (wellId: number) =>
    apiClient.get<ListResponse<CloudProcessItem>>(`/api/DirllSurvay/GetAllCloudProcessByWellId?wellId=${wellId}`).then(r => r.data),
  getAllBySiteId: (siteId: number, q: ListQuery = {}) =>
    apiClient.get<ListResponse<CloudProcessItem>>(`/api/DirllSurvay/GetAllCloudProcessBySite${queryString({ ...q, siteId })}`).then(r => r.data),
  getById: (cloudProcessId: number) =>
    apiClient.get<EntityResponse<CloudProcessItem>>(`/api/DirllSurvay/GetALlCloudProcessFromId?cloudProcess=${cloudProcessId}`).then(r => r.data),
  process: (entity: Record<string, unknown>) =>
    apiClient.post<EntityResponse<CloudProcessItem>>('/api/DirllSurvay/PerfectCloud', entity).then(r => r.data),
  delete: (id: number) =>
    apiClient.post<EntityResponse<CloudProcessItem>>('/api/DirllSurvay/DeleteCloudProcess', { id, wellId: 0, drillSurvayId: 0, wellGeonomicId: 0, cloudHeadId: 0, perfectCloudStatus: 0, actualCloudStatus: 0, plyFileStatus: 0, description: '', fromLength: 0, toLength: 0 }).then(r => r.data),
  downloadPlyFile: (fileId: number, cloudType: string) =>
    apiClient.get(`/api/DirllSurvay/DownloadPlyFile?fileId=${fileId}&cloudType=${cloudType}`, {
      responseType: 'blob',
    }).then(r => r.data as Blob),
  getGeonomicFiles: (wellId: number) =>
    apiClient.get<ListResponse<GeonomicFileItem>>(`/api/DirllSurvay/GetWellGonomicFile?wellId=${wellId}`).then(r => r.data),
};

// ---- Dashboard ----
export const dashboardApi = {
  getSummary: () =>
    apiClient.get<EntityResponse<SummaryDashboard>>('/api/dashboards/getsummarydashboard').then(r => r.data),
  getSummaryByCompany: (companyId: number) =>
    apiClient.get<EntityResponse<SummaryDashboard>>(`/api/dashboards/getsummarydashboardbycompanyid?companyId=${companyId}`).then(r => r.data),
};
